import {
  FC,
  PropsWithChildren,
  createContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Loader } from '@gasbi/shared';
import OrganizationManager from '@gasbi/shared/common/utils/organizationManager';
import { NotAuthorizedView } from '@gasbi/shared/web/accessPages/NotAuthorizedView';

import { useLazyGetCurrentUserQuery } from '@/features/auth/authApi';
import { User } from '@/features/auth/types';
import http from '@/utils/http';

export interface AuthContextValue {
  user: User;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Cookie (BFF) session bootstrap (GSB-7613): the Households API owns the OIDC flow, the
 * browser holds only the HttpOnly session cookie. One GET /api/user loads the current user;
 * a 401 is handled by the http interceptor (navigation to /api/login?returnUrl=...); a 403 means
 * the identity signed in fine but has no access to Households (GSB-8504): the shared "no access" page.
 * Then one GET /api/session/organization (GSB-8274, PO 2026-10-25): the organization the user
 * works in lives in the session, the landing opens it. Without the endpoint (404 while the
 * Households API of GSB-8276 is not deployed) or without an organization the landing decides as before.
 */
export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [fetchCurrentUser, { data: user, isSuccess, error }] =
    useLazyGetCurrentUserQuery();
  const [isOrganizationReady, setIsOrganizationReady] = useState(false);

  const isForbidden =
    (error as { status?: number } | undefined)?.status === 403;

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  useEffect(() => {
    if (!user) return;

    const onSettled = () => setIsOrganizationReady(true);
    OrganizationManager.initialize({ http }).then(onSettled, onSettled);
  }, [user]);

  const value = useMemo(
    () =>
      user && {
        user,
        logout: () => {
          window.location.assign('/api/logout?returnUrl=/');
        },
      },
    [user],
  );

  if (isForbidden) {
    return <NotAuthorizedView />;
  }

  if (!isSuccess || !value || !isOrganizationReady) {
    return <Loader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
